import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query"
import { summarizeNode } from "@velizhanin-dev/bot-scenario"
import { Command as CommandPrimitive } from "cmdk"
import {
	Bot,
	FileText,
	Link2,
	type LucideIcon,
	Megaphone,
	Navigation,
	Search,
	SquareStack,
	User,
	Users,
	Workflow,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { getAdminPosts } from "@/entities/admin-post"
import { audienceKeys, listAudience, memberName } from "@/entities/audience"
import { botBuilderKeys, type CustomBot, getBot, getScenario, listBots } from "@/entities/bot-builder"
import { broadcastKeys, listBroadcasts } from "@/entities/broadcast"
import { useSessionRole } from "@/entities/session"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { OPEN_COMMAND_PALETTE } from "@/shared/lib/command-palette"
import { type RecentItem, readRecentItems } from "@/shared/lib/recent-items"
import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog"
import { botPath, WORKSPACE_NAV } from "@/widgets/bot-workspace"

const SECTIONS = [
	{ label: "Контент", to: "/admin" },
	{ label: "Аналитика приложения", to: "/admin/analytics" },
	{ label: "Боты", to: "/admin/bots" },
	{ label: "Классификаторы", to: "/admin/taxonomy" },
	{ label: "MCP", to: "/admin/mcp" },
]

const SERVER_MIN = 2
const LIMIT = 5
/** Вне бота ищем по стольким рабочим ботам сразу. */
const MAX_BOTS = 5

interface Hit {
	id: string
	label: string
	hint?: string
	to: string
	/** Состояние перехода: например, какой блок воронки показать. */
	state?: unknown
	icon: LucideIcon
}

const matches = (query: string, ...values: (string | null | undefined)[]) =>
	!query || values.some((value) => value?.toLowerCase().includes(query))

/**
 * Поиск по бэкофису (⌘K): люди, воронки и их блоки по тексту, UTM-метки, группы и рассылки (внутри бота только его,
 * на общих страницах всех рабочих ботов), а ещё боты, материалы и разделы. Пустое поле
 * показывает недавно открытых людей и рассылки.
 */
export function CommandPalette() {
	const { isModerator } = useSessionRole()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")
	const [selected, setSelected] = useState("")
	const term = query.trim().toLowerCase()
	const debounced = useDebounced(query.trim(), 250)
	const botId = /^\/admin\/bots\/([^/]+)/.exec(pathname)?.[1] ?? ""
	const server = open && debounced.length >= SERVER_MIN

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			// ⌘K в поле текста уже стал ссылкой: редактор отметил событие обработанным.
			if (event.defaultPrevented) return
			if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
				event.preventDefault()
				setOpen((value) => !value)
			}
		}
		const onOpen = () => setOpen(true)
		window.addEventListener("keydown", onKey)
		window.addEventListener(OPEN_COMMAND_PALETTE, onOpen)
		return () => {
			window.removeEventListener("keydown", onKey)
			window.removeEventListener(OPEN_COMMAND_PALETTE, onOpen)
		}
	}, [])
	useEffect(() => {
		if (!open) setQuery("")
	}, [open])

	const enabled = open && isModerator
	const bots = useQuery({ queryKey: botBuilderKeys.bots, queryFn: listBots, enabled })
	const bot = useQuery({
		queryKey: botBuilderKeys.bot(botId),
		queryFn: () => getBot(botId),
		enabled: enabled && !!botId,
	})
	// В боте ищем по нему, на общих страницах по всем рабочим ботам сразу.
	const targets: CustomBot[] = botId
		? bot.data
			? [bot.data]
			: []
		: (bots.data ?? []).slice(0, MAX_BOTS)
	const several = targets.length > 1
	const people = useQueries({
		queries: targets.map((target) => ({
			queryKey: [...audienceKeys.list(target.id, { search: debounced }), "palette"],
			queryFn: () => listAudience(target.id, { search: debounced }, { skip: 0, take: LIMIT }),
			enabled: server && isModerator,
			placeholderData: keepPreviousData,
		})),
	})
	const broadcasts = useQueries({
		queries: targets.map((target) => ({
			queryKey: broadcastKeys.list(target.id),
			queryFn: () => listBroadcasts(target.id),
			enabled: enabled && (!!botId || !!term),
		})),
	})
	// Черновик открытого бота уже загружен его разделом и хранит несохранённые правки: его
	// только читаем из кеша, свой запрос мог бы их перезаписать. Чужие боты читаем отдельно.
	const scenarios = useQueries({
		queries: targets.map((target) => {
			const id = target.scenarioId ?? ""
			const current = target.id === botId
			return {
				queryKey: current ? botBuilderKeys.scenario(id) : ["palette", "scenario", id],
				queryFn: () => getScenario(id),
				enabled: !current && enabled && !!term && !!id,
				staleTime: 60_000,
			}
		}),
	})
	const posts = useQuery({
		queryKey: ["palette", "posts", debounced],
		queryFn: () => getAdminPosts({ search: debounced, limit: LIMIT }),
		enabled: server && isModerator,
		placeholderData: keepPreviousData,
	})
	const [recent, setRecent] = useState<RecentItem[]>([])
	useEffect(() => {
		if (open) setRecent(readRecentItems())
	}, [open])

	const peopleData = people.map((result) => result.data)
	const broadcastData = broadcasts.map((result) => result.data)
	const scenarioData = scenarios.map((result) => result.data)
	const groups = (() => {
		const list: { heading: string; hits: Hit[] }[] = []
		const withBot = (hint: string | undefined, target: CustomBot) =>
			several ? [hint, target.name].filter(Boolean).join(" · ") : hint
		const across = <T,>(pick: (target: CustomBot, index: number) => T[]) =>
			targets.flatMap((target, index) => pick(target, index))

		if (!term && recent.length > 0) {
			list.push({
				heading: "Недавние",
				hits: recent
					.filter((item) => !botId || item.to.startsWith(botPath(botId)))
					.slice(0, 6)
					.map((item) => ({
						id: `recent-${item.to}`,
						label: item.label,
						hint: item.hint,
						to: item.to,
						icon: item.kind === "person" ? User : item.kind === "funnel" ? Workflow : Megaphone,
					})),
			})
		}
		if (botId) {
			list.push({
				heading: `В боте ${bot.data?.name ?? ""}`.trim(),
				hits: WORKSPACE_NAV.flatMap((section) =>
					section.items
						.filter((item) => matches(term, item.label, section.title, item.description))
						.map((item) => ({
							id: `nav-${item.to}`,
							label: item.label,
							hint: section.title,
							to: botPath(botId, item.to),
							icon: Navigation,
						})),
				).slice(0, term ? 6 : 12),
			})
		}
		if (server) {
			list.push({
				heading: "Люди",
				hits: across((target, index) =>
					(peopleData[index]?.items ?? []).map((member) => ({
						id: `person-${target.id}-${member.id}`,
						label: memberName(member),
						hint: withBot(member.username ? `@${member.username}` : undefined, target),
						to: botPath(target.id, `analytics/users/${member.id}`),
						icon: User,
					})),
				).slice(0, LIMIT * 2),
			})
		}
		if (term) {
			list.push({
				heading: "UTM-метки",
				hits: across((target, index) =>
					(scenarioData[index]?.entryLinks ?? [])
						.filter((link) => matches(term, link.name, link.code))
						.slice(0, LIMIT)
						.map((link) => ({
							id: `link-${target.id}-${link.code}`,
							label: link.name || link.code,
							hint: withBot(link.code, target),
							to: botPath(target.id, `analytics/utm/${encodeURIComponent(link.code)}`),
							icon: Link2,
						})),
				),
			})
			list.push({
				heading: "Группы",
				hits: across((target, index) =>
					(scenarioData[index]?.tags ?? [])
						.filter((tag) => matches(term, tag.name))
						.slice(0, LIMIT)
						.map((tag) => ({
							id: `group-${target.id}-${tag.id}`,
							label: tag.name,
							hint: withBot("люди в группе", target),
							to: `${botPath(target.id, "analytics/users")}?group=${encodeURIComponent(tag.id)}`,
							icon: Users,
						})),
				),
			})
			list.push({
				heading: "Воронки",
				hits: across((target, index) =>
					(scenarioData[index]?.flows ?? [])
						.filter((flow) => matches(term, flow.name))
						.slice(0, LIMIT)
						.map((flow) => ({
							id: `funnel-${target.id}-${flow.id}`,
							label: flow.name,
							hint: withBot(flow.active === false ? "выключена" : undefined, target),
							to: botPath(target.id, `constructor/funnels/${flow.id}`),
							icon: Workflow,
						})),
				),
			})
			if (term.length >= SERVER_MIN) {
				list.push({
					heading: "Блоки воронок",
					hits: across((target, index) => {
						const draft = scenarioData[index]
						if (!draft) return []
						const flowName = new Map(draft.flows.map((flow) => [flow.id, flow.name]))
						return draft.nodes
							.map((node) => ({
								node,
								text: summarizeNode(node, draft.flows, draft.tags).replace(/\s+/g, " ").trim(),
							}))
							.filter(({ text }) => matches(term, text))
							.slice(0, LIMIT)
							.map(({ node, text }) => ({
								id: `node-${target.id}-${node.id}`,
								label: text.length > 70 ? `${text.slice(0, 70)}…` : text,
								hint: withBot(flowName.get(node.flowId), target),
								to: botPath(target.id, `constructor/funnels/${node.flowId}`),
								state: { focusNodeId: node.id },
								icon: SquareStack,
							}))
					}),
				})
			}
			list.push({
				heading: "Рассылки",
				hits: across((target, index) =>
					(broadcastData[index] ?? [])
						.filter((item) => matches(term, item.name))
						.slice(0, LIMIT)
						.map((item) => ({
							id: `broadcast-${item.id}`,
							label: item.name,
							hint: withBot(undefined, target),
							to: botPath(target.id, `broadcasts/${item.id}`),
							icon: Megaphone,
						})),
				),
			})
		}
		list.push({
			heading: "Боты",
			hits: (bots.data ?? [])
				.filter((item) => term && matches(term, item.name, item.username))
				.slice(0, LIMIT)
				.map((item) => ({
					id: `bot-${item.id}`,
					label: item.name,
					hint: `@${item.username}`,
					to: botPath(item.id),
					icon: Bot,
				})),
		})
		if (server) {
			list.push({
				heading: "Материалы",
				hits: (posts.data?.posts ?? []).map((post) => ({
					id: `post-${post.id}`,
					label: post.title || "Без названия",
					to: `/admin/posts/${post.id}`,
					icon: FileText,
				})),
			})
		}
		list.push({
			heading: "Разделы",
			hits: SECTIONS.filter((item) => matches(term, item.label)).map((item) => ({
				id: `section-${item.to}`,
				label: item.label,
				to: item.to,
				icon: Navigation,
			})),
		})
		return list.filter((group) => group.hits.length > 0)
	})()

	const ids = groups.flatMap((group) => group.hits.map((hit) => hit.id))
	// Результаты приходят с сервера позже ввода, и cmdk теряет выделение: тогда Enter ничего
	// не открывает. Держим выделенным первый результат, пока выбранного нет в списке.
	const active = ids.includes(selected) ? selected : (ids[0] ?? "")

	if (!isModerator) return null
	const loading = server && (people.some((result) => result.isFetching) || posts.isFetching)

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent className="top-[20%] translate-y-0 overflow-hidden p-0 sm:max-w-xl">
				<DialogTitle className="sr-only">Поиск</DialogTitle>
				<CommandPrimitive
					shouldFilter={false}
					loop
					value={active}
					onValueChange={setSelected}
					className="flex flex-col"
				>
					<div className="flex items-center gap-2 border-b border-border px-3">
						<Search className="size-4 shrink-0 text-muted-foreground" />
						<CommandPrimitive.Input
							autoFocus
							value={query}
							onValueChange={setQuery}
							placeholder={
								botId ? "Человек, воронка, текст блока, метка, рассылка…" : "Человек, воронка, рассылка, бот, материал…"
							}
							className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
						/>
					</div>
					<CommandPrimitive.List className="max-h-[60vh] overflow-y-auto p-2">
						<CommandPrimitive.Empty className="py-8 text-center text-sm text-muted-foreground">
							{loading ? "Ищем…" : "Ничего не нашлось"}
						</CommandPrimitive.Empty>
						{groups.map((group) => (
							<CommandPrimitive.Group
								key={group.heading}
								heading={group.heading}
								className="mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-muted-foreground"
							>
								{group.hits.map((hit) => (
									<CommandPrimitive.Item
										key={hit.id}
										value={hit.id}
										onSelect={() => {
											setOpen(false)
											navigate(hit.to, { state: hit.state })
										}}
										className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm data-[selected=true]:bg-accent"
									>
										<hit.icon className="size-4 shrink-0 text-muted-foreground" />
										<span className="min-w-0 flex-1 truncate">{hit.label}</span>
										{hit.hint ? (
											<span className="max-w-56 shrink-0 truncate text-xs text-muted-foreground">
												{hit.hint}
											</span>
										) : null}
									</CommandPrimitive.Item>
								))}
							</CommandPrimitive.Group>
						))}
					</CommandPrimitive.List>
					{!server ? (
						<p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
							Наберите 2 буквы, чтобы искать людей и материалы
						</p>
					) : null}
				</CommandPrimitive>
			</DialogContent>
		</Dialog>
	)
}
